/**
 * Documentos (PCMSO/PGR) — Importação Inteligente.
 * Seletor de empresa → lista de documentos com status da extração IA
 * (polling enquanto processa) + upload de PDF + reprocessar/revisar.
 */
import { useRef, useState } from "react";

import { GhesAptidoesModal } from "@/components/GhesAptidoesModal";
import { Link, useLocation } from "react-router-dom";

import {
  useDocumentos,
  useEmpresas,
  useExcluirDocumento,
  useInativarDocumento,
  useReativarDocumento,
  useTrocarMedicoCoordenador,
  useReprocessarDocumento,
  useUploadDocumento,
} from "@/hooks/useApi";
import type { DocumentoPrograma, StatusDocumento, TipoDocumento } from "@/types/extracao";
import { rotuloEscopo } from "@/types/extracao";

const STATUS_BADGE: Record<StatusDocumento, { rotulo: string; classe: string }> = {
  rascunho: { rotulo: "rascunho", classe: "badge bg-gray-100 text-ink-muted" },
  em_revisao: { rotulo: "em revisão", classe: "badge bg-amber-100 text-amber-800" },
  aprovado: { rotulo: "aprovado", classe: "badge bg-emerald-100 text-emerald-800" },
  substituido: { rotulo: "substituído", classe: "badge bg-gray-100 text-ink-subtle line-through" },
  // Fora de vigência por decisão humana — neutro, não é erro; mas bem visível.
  inativo: {
    rotulo: "inativo",
    classe: "badge bg-gray-200 font-semibold text-ink ring-1 ring-inset ring-gray-400",
  },
};

function fmtData(iso: string | null): string {
  if (!iso) return "—";
  const [a, m, d] = iso.split("T")[0].split("-");
  return `${d}/${m}/${a}`;
}

function ExtracaoStatus({ doc }: { doc: DocumentoPrograma }) {
  switch (doc.extracao_status) {
    case "processando":
      return (
        <span className="inline-flex items-center gap-2 text-xs text-ink-muted">
          <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-anarosa border-t-transparent" />
          extraindo…
        </span>
      );
    case "concluida":
      return <span className="badge bg-emerald-50 text-emerald-700">extração concluída</span>;
    case "erro":
      return (
        <span className="badge bg-red-100 text-red-800" title={doc.extracao_erro ?? undefined}>
          erro na extração
        </span>
      );
    default:
      return <span className="badge bg-gray-100 text-ink-muted">pendente</span>;
  }
}

export function DocumentosPage() {
  const location = useLocation();
  const msgSucesso = (location.state as { msg?: string } | null)?.msg;

  const { data: empresas } = useEmpresas();
  const [empresaId, setEmpresaId] = useState<number | null>(null);
  const { data: docs, isLoading } = useDocumentos(empresaId);

  const upload = useUploadDocumento();
  const reprocessar = useReprocessarDocumento();
  const inativar = useInativarDocumento();
  const reativar = useReativarDocumento();
  const excluir = useExcluirDocumento();

  // Troca do médico responsável pelo PCMSO. Vale em documento APROVADO: a
  // empresa troca o coordenador sem emitir documento novo, e é esse nome que
  // sai impresso em todo ASO novo.
  const trocarMedico = useTrocarMedicoCoordenador();
  const [medicoDoc, setMedicoDoc] = useState<DocumentoPrograma | null>(null);
  const [ghesDoc, setGhesDoc] = useState<DocumentoPrograma | null>(null);
  const [medicoForm, setMedicoForm] = useState({ nome: "", crm: "", uf: "", todos: false });

  const [tipo, setTipo] = useState<TipoDocumento>("PCMSO");
  const [escopo, setEscopo] = useState("");
  const [arquivo, setArquivo] = useState<File | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function handleUpload() {
    if (empresaId === null || !arquivo) return;
    try {
      await upload.mutateAsync({ empresaId, tipo, escopo, arquivo });
      setArquivo(null);
      setEscopo("");
      if (fileRef.current) fileRef.current.value = "";
    } catch {
      /* erro exibido via upload.error */
    }
  }

  /** Só um erro de vigência por vez na tela: limpa os anteriores antes de agir. */
  function limparErrosVigencia() {
    inativar.reset();
    reativar.reset();
    excluir.reset();
  }

  function handleInativar(d: DocumentoPrograma) {
    limparErrosVigencia();
    const ok = window.confirm(
      `Inativar o ${d.tipo} de "${rotuloEscopo(d.escopo)}"?\n\n` +
        "O documento sai de vigência e deixa de gerar pendências de exame. " +
        "Ele continua no histórico (os ASOs já emitidos seguem rastreáveis) e " +
        "pode ser reativado depois."
    );
    if (ok) inativar.mutate(d.id);
  }

  function handleReativar(d: DocumentoPrograma) {
    limparErrosVigencia();
    reativar.mutate(d.id);
  }

  function handleExcluir(d: DocumentoPrograma) {
    if (empresaId === null) return;
    limparErrosVigencia();
    const ok = window.confirm(
      `Excluir o ${d.tipo} de "${rotuloEscopo(d.escopo)}"?\n\n` +
        "O PDF e a extração da IA serão apagados. Isso NÃO tem volta. " +
        "Use só para upload errado, duplicado ou extração inaproveitável."
    );
    if (ok) excluir.mutate({ id: d.id, empresaId });
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-2xl font-semibold">Documentos (PCMSO/PGR)</h2>
        <p className="text-sm text-ink-muted">
          Importação Inteligente: upload do PDF, extração IA e revisão/aprovação da base
          estruturada (GHEs, riscos, exigências de exames). Nada vale sem aprovação humana.
        </p>
      </div>

      {msgSucesso ? (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
          {msgSucesso}
        </div>
      ) : null}

      <div className="max-w-md">
        <label className="mb-1 block text-xs font-medium text-ink-muted">Empresa</label>
        <select
          className="input"
          value={empresaId ?? ""}
          onChange={(e) => setEmpresaId(e.target.value ? Number(e.target.value) : null)}
        >
          <option value="">selecione a empresa…</option>
          {(empresas ?? []).map((e) => (
            <option key={e.id} value={e.id}>{e.razao_social}</option>
          ))}
        </select>
      </div>

      {empresaId === null ? (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <strong>Selecione a empresa acima</strong> para enviar um PCMSO ou PGR e ver os
          documentos já importados. O formulário de upload aparece depois da escolha.
        </div>
      ) : null}

      {empresaId !== null ? (
        <div className="card p-4">
          <h3 className="font-display text-base font-semibold">Enviar documento</h3>
          <p className="text-xs text-ink-muted">
            PDF do PCMSO ou PGR (máx. 30 MB). A extração IA inicia automaticamente. A empresa pode
            ter vários documentos vigentes ao mesmo tempo — um por contrato/lotação (escopo).
          </p>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-ink-muted">Tipo</label>
              <select className="input w-36" value={tipo}
                onChange={(e) => setTipo(e.target.value as TipoDocumento)}>
                <option value="PCMSO">PCMSO</option>
                <option value="PGR">PGR</option>
              </select>
            </div>
            <div className="min-w-72 flex-1">
              <label className="mb-1 block text-xs font-medium text-ink-muted">
                Escopo / lotação (contrato) — opcional
              </label>
              <input
                className="input"
                value={escopo}
                onChange={(e) => setEscopo(e.target.value)}
                placeholder="Braskem SP — deixe vazio se o PCMSO vale para a empresa toda"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-ink-muted">Arquivo (PDF)</label>
              <input
                ref={fileRef}
                type="file"
                accept="application/pdf,.pdf"
                className="text-sm text-ink-muted file:mr-3 file:rounded-md file:border-0 file:bg-anarosa-50 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-anarosa-700 hover:file:bg-anarosa-100"
                onChange={(e) => setArquivo(e.target.files?.[0] ?? null)}
              />
            </div>
            <button className="btn-primary" disabled={!arquivo || upload.isPending} onClick={handleUpload}>
              {upload.isPending ? "Enviando…" : "Enviar e extrair"}
            </button>
          </div>
          {upload.error ? (
            <p className="mt-2 text-sm text-anarosa">{(upload.error as Error).message}</p>
          ) : null}
        </div>
      ) : null}

      {empresaId !== null && isLoading ? <p className="text-ink-muted">Carregando…</p> : null}

      {empresaId !== null && docs && docs.length > 0 ? (
        <div className="card overflow-hidden">
          <div className="border-b border-gray-200 bg-gray-50 px-4 py-3">
            <h3 className="font-display text-base font-semibold">Documentos importados</h3>
            <p className="text-xs text-ink-muted">
              Renovação do mesmo contrato: envie o novo PDF com o <strong>mesmo escopo</strong> — ao
              aprovar, o anterior passa a substituído sozinho. Se a empresa unificou vários PCMSOs
              num só, ou repartiu um em vários por lotação, os escopos não se correspondem:{" "}
              <strong>inative</strong> os que não valem mais e aprove os novos. Inativo sai de
              vigência e para de gerar pendências, mas continua no histórico e pode ser reativado.
              Excluir só serve pra documento ainda em rascunho ou em revisão.
            </p>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-ink-muted">
              <tr>
                <th className="px-4 py-3">Tipo</th>
                <th className="px-4 py-3">Escopo / lotação</th>
                <th className="px-4 py-3">Revisão</th>
                <th className="px-4 py-3">Vigência</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Extração IA</th>
                <th className="px-4 py-3">Enviado em</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {docs.map((d) => (
                <tr key={d.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 font-medium">{d.tipo}</td>
                  <td className={`px-4 py-2 ${d.escopo ? "" : "italic text-ink-subtle"}`}
                    title={d.escopo || "vale para a empresa toda"}>
                    {rotuloEscopo(d.escopo)}
                  </td>
                  <td className="px-4 py-2 text-ink-muted" title={d.revisao ?? undefined}>
                    {d.revisao ? (d.revisao.length > 40 ? `${d.revisao.slice(0, 40)}…` : d.revisao) : "—"}
                  </td>
                  <td className="px-4 py-2 text-ink-muted">
                    {d.vigencia_inicio ? `${fmtData(d.vigencia_inicio)} a ${fmtData(d.vigencia_fim)}` : "—"}
                  </td>
                  <td className="px-4 py-2">
                    <span className={STATUS_BADGE[d.status].classe}>{STATUS_BADGE[d.status].rotulo}</span>
                  </td>
                  <td className="px-4 py-2"><ExtracaoStatus doc={d} /></td>
                  <td className="px-4 py-2 text-ink-muted">{fmtData(d.criado_em)}</td>
                  <td className="px-4 py-2 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {d.extracao_status === "erro" &&
                      (d.status === "rascunho" || d.status === "em_revisao") ? (
                        <button
                          className="btn-secondary px-2 py-1 text-xs"
                          disabled={reprocessar.isPending}
                          onClick={() => reprocessar.mutate(d.id)}
                        >
                          Reprocessar
                        </button>
                      ) : null}
                      {d.extracao_status === "concluida" &&
                      (d.status === "rascunho" || d.status === "em_revisao") ? (
                        <Link className="btn-primary px-2 py-1 text-xs" to={`/documentos/${d.id}/revisao`}>
                          Revisar
                        </Link>
                      ) : null}
                      {d.status === "aprovado" || d.status === "inativo" ? (
                        <Link className="btn-secondary px-2 py-1 text-xs" to={`/documentos/${d.id}/revisao`}>
                          Ver
                        </Link>
                      ) : null}
                      {d.status === "aprovado" || d.status === "rascunho" ||
                       d.status === "em_revisao" ? (
                        <button
                          className="btn-secondary px-2 py-1 text-xs"
                          onClick={() => {
                            setMedicoForm({
                              nome: d.medico_coordenador_nome ?? "",
                              crm: d.medico_coordenador_crm ?? "",
                              uf: d.medico_coordenador_uf ?? "",
                              todos: false,
                            });
                            setMedicoDoc(d);
                          }}
                        >
                          Médico
                        </button>
                      ) : null}
                      {d.status === "aprovado" || d.status === "rascunho" ||
                       d.status === "em_revisao" ? (
                        <button
                          className="btn-secondary px-2 py-1 text-xs"
                          onClick={() => setGhesDoc(d)}
                        >
                          Aptidões
                        </button>
                      ) : null}
                      {d.status === "aprovado" ? (
                        <button
                          className="btn-secondary px-2 py-1 text-xs"
                          disabled={inativar.isPending}
                          onClick={() => handleInativar(d)}
                        >
                          Inativar
                        </button>
                      ) : null}
                      {d.status === "inativo" ? (
                        <button
                          className="btn-primary px-2 py-1 text-xs"
                          disabled={reativar.isPending}
                          onClick={() => handleReativar(d)}
                        >
                          Reativar
                        </button>
                      ) : null}
                      {d.status === "rascunho" || d.status === "em_revisao" ? (
                        <button
                          className="btn-secondary px-2 py-1 text-xs text-anarosa"
                          disabled={excluir.isPending}
                          onClick={() => handleExcluir(d)}
                        >
                          Excluir
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {reprocessar.error ? (
            <p className="px-4 pb-3 text-sm text-anarosa">{(reprocessar.error as Error).message}</p>
          ) : null}
          {/* Os 409 de inativar/reativar/excluir são informativos (ex.: conflito de
              escopo ao reativar) — a mensagem do backend precisa aparecer na tela. */}
          {inativar.error || reativar.error || excluir.error ? (
            <div className="mx-4 mb-3 rounded-md border border-anarosa-200 bg-anarosa-50 p-3 text-sm text-anarosa-700">
              {((inativar.error ?? reativar.error ?? excluir.error) as Error).message}
            </div>
          ) : null}
        </div>
      ) : empresaId !== null && !isLoading ? (
        <p className="text-sm text-ink-muted">Nenhum documento enviado pra essa empresa ainda.</p>
      ) : null}

      {ghesDoc ? (
        <GhesAptidoesModal documento={ghesDoc} onClose={() => setGhesDoc(null)} />
      ) : null}

      {/* ---------- Troca do médico responsável pelo PCMSO ---------- */}
      {medicoDoc ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => setMedicoDoc(null)}>
          <div className="card w-full max-w-md space-y-3 p-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <h3 className="font-display text-lg font-semibold">Médico responsável pelo PCMSO</h3>
              <button className="text-ink-muted hover:text-ink"
                onClick={() => setMedicoDoc(null)}>✕</button>
            </div>
            <p className="text-xs text-ink-muted">
              {medicoDoc.tipo} · {rotuloEscopo(medicoDoc.escopo)}
            </p>

            <div>
              <label className="text-xs uppercase tracking-wider text-ink-muted">Nome</label>
              <input className="input mt-1" value={medicoForm.nome} autoFocus
                onChange={(e) => setMedicoForm({ ...medicoForm, nome: e.target.value })} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <label className="text-xs uppercase tracking-wider text-ink-muted">CRM</label>
                <input className="input mt-1" value={medicoForm.crm}
                  onChange={(e) => setMedicoForm({ ...medicoForm, crm: e.target.value })} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-ink-muted">UF</label>
                <input className="input mt-1" maxLength={2} value={medicoForm.uf}
                  onChange={(e) =>
                    setMedicoForm({ ...medicoForm, uf: e.target.value.toUpperCase() })} />
              </div>
            </div>

            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" className="mt-1" checked={medicoForm.todos}
                onChange={(e) => setMedicoForm({ ...medicoForm, todos: e.target.checked })} />
              <span>
                Aplicar aos <strong>demais documentos aprovados desta empresa</strong> que hoje
                apontam para o mesmo médico.
                <span className="block text-xs text-ink-muted">
                  Contrato com coordenador diferente não é alterado.
                </span>
              </span>
            </label>

            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              Vale para os ASOs emitidos <strong>daqui em diante</strong>. ASOs já emitidos
              não mudam — o documento é congelado na emissão.
            </div>

            <div className="flex items-center justify-end gap-2">
              <button className="btn-secondary" onClick={() => setMedicoDoc(null)}>Cancelar</button>
              <button className="btn-primary"
                disabled={trocarMedico.isPending || medicoForm.nome.trim().length < 3 ||
                          medicoForm.crm.trim().length < 2 || medicoForm.uf.trim().length !== 2}
                onClick={async () => {
                  try {
                    const docs = await trocarMedico.mutateAsync({
                      id: medicoDoc.id,
                      nome: medicoForm.nome.trim(),
                      crm: medicoForm.crm.trim(),
                      uf: medicoForm.uf.trim(),
                      aplicar_em_todos_da_empresa: medicoForm.todos,
                    });
                    setMedicoDoc(null);
                    window.alert(
                      docs.length > 1
                        ? `Médico atualizado em ${docs.length} documentos.`
                        : "Médico atualizado.",
                    );
                  } catch { /* erro abaixo */ }
                }}>
                {trocarMedico.isPending ? "Salvando…" : "Salvar"}
              </button>
            </div>
            {trocarMedico.error ? (
              <p className="text-sm text-anarosa">{(trocarMedico.error as Error).message}</p>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
